import { useState, useEffect, useCallback, useRef } from 'react'
import api from '../services/api'

// Category display order — bones first (expanded), then soft tissue (collapsed)
const BONE_CATEGORIES = ['Skull & Face', 'Spine', 'Ribs', 'Upper Limb', 'Lower Limb']
const SOFT_CATEGORIES = ['Organs', 'Cardiovascular', 'Respiratory', 'Muscles', 'Other Soft Tissue']
const CATEGORY_ORDER = [...BONE_CATEGORIES, ...SOFT_CATEGORIES]

// Structure colors for 3D viewer (consistent per category)
const CATEGORY_COLORS = {
  'Skull & Face': '#e8d5b7',
  'Spine': '#b8c4d4',
  'Ribs': '#c4b8a8',
  'Upper Limb': '#d4c4a8',
  'Lower Limb': '#c8b898',
  'Organs': '#d4887a',
  'Cardiovascular': '#e85454',
  'Respiratory': '#88b4d8',
  'Muscles': '#c87878',
  'Other Soft Tissue': '#b898b8',
}

export default function SegmentPanel({ caseId, seriesId, onClose }) {
  const [structures, setStructures] = useState(null)   // from /structures endpoint
  const [selected, setSelected] = useState(new Set())   // selected structure IDs
  const [expanded, setExpanded] = useState(new Set(BONE_CATEGORIES)) // expanded categories
  const [fast, setFast] = useState(false)

  // Job state
  const [jobId, setJobId] = useState(null)
  const [jobStatus, setJobStatus] = useState(null)
  const [polling, setPolling] = useState(false)
  const pollRef = useRef(null)

  // Fetch available structures on mount + fire warmup
  useEffect(() => {
    api.get('/segment/structures')
      .then((res) => {
        setStructures(res.data)
        // Default: select all bone IDs
        if (res.data.bone_ids) {
          setSelected(new Set(res.data.bone_ids))
        }
      })
      .catch(() => {
        // Fallback: service not available yet
        setStructures({ categories: {}, bone_ids: [], total_count: 0 })
      })

    // Warmup: wake the segment Cloud Run container while user browses
    api.post(`/segment/${caseId}/warmup`).catch(() => {})
  }, [caseId])

  // Toggle a single structure
  const toggleStructure = (id) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Toggle entire category
  const toggleCategory = (category) => {
    if (!structures?.categories?.[category]) return
    const ids = structures.categories[category].map((s) => s.id)
    const allSelected = ids.every((id) => selected.has(id))
    setSelected((prev) => {
      const next = new Set(prev)
      if (allSelected) {
        ids.forEach((id) => next.delete(id))
      } else {
        ids.forEach((id) => next.add(id))
      }
      return next
    })
  }

  // Expand/collapse category
  const toggleExpand = (category) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(category)) next.delete(category)
      else next.add(category)
      return next
    })
  }

  // Quick select shortcuts
  const selectAllBones = () => {
    if (structures?.bone_ids) setSelected(new Set(structures.bone_ids))
  }
  const selectAll = () => {
    if (!structures?.categories) return
    const all = Object.values(structures.categories).flat().map((s) => s.id)
    setSelected(new Set(all))
  }
  const selectNone = () => setSelected(new Set())

  // Start segmentation
  const startSegmentation = useCallback(async () => {
    if (selected.size === 0) return
    try {
      const structureList = [...selected]
      const res = await api.post(`/segment/${caseId}/start`, {
        seriesId,
        structures: structureList,
        fast,
      })
      setJobId(res.data.jobId)
      setPolling(true)
      setJobStatus({
        status: 'downloading',
        progress: 0,
        phase: 'Starting...',
        estimatedCost: res.data.estimatedCost,
      })
    } catch (err) {
      setJobStatus({
        status: 'failed',
        error: err.response?.data?.error || err.message,
      })
    }
  }, [caseId, seriesId, selected, fast])

  // Poll for status
  useEffect(() => {
    if (!polling || !jobId) return

    const poll = async () => {
      try {
        const res = await api.get(`/segment/${caseId}/status/${jobId}`)
        setJobStatus(res.data)
        if (res.data.status === 'complete' || res.data.status === 'failed') {
          setPolling(false)
        }
      } catch {
        // Keep polling on network errors
      }
    }

    poll()
    pollRef.current = setInterval(poll, 4000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [polling, jobId, caseId])

  const isRunning = jobStatus && !['complete', 'failed'].includes(jobStatus.status)
  const categories = structures?.categories || {}

  return (
    <div className="fixed inset-y-0 right-0 w-[420px] bg-gray-800 border-l border-gray-700 shadow-2xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
        <div>
          <h2 className="text-white font-semibold text-lg">AI Segment</h2>
          <p className="text-xs text-gray-400 mt-0.5">TotalSegmentator — 117 structures</p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
          title="Close panel"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {/* Cost/time warning */}
        <div className="rounded-lg bg-amber-900/20 border border-amber-700/50 px-4 py-3">
          <p className="text-xs text-amber-300 font-medium">GPU Cloud Processing</p>
          <p className="text-[11px] text-amber-400/70 mt-1">
            Estimated cost: ~{fast ? '$0.03-0.08' : '$0.05-0.15'} per scan.
            Time: ~{fast ? '2-5' : '5-10'} min. Billed per use.
          </p>
        </div>

        {/* Quick select buttons */}
        {!isRunning && !jobStatus?.results && (
          <div className="flex gap-2">
            <button onClick={selectAllBones} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs rounded-lg transition-colors">
              All Bones ({structures?.bone_ids?.length || 0})
            </button>
            <button onClick={selectAll} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs rounded-lg transition-colors">
              All ({structures?.total_count || 0})
            </button>
            <button onClick={selectNone} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs rounded-lg transition-colors">
              None
            </button>
            <div className="flex-1" />
            <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={fast}
                onChange={(e) => setFast(e.target.checked)}
                className="accent-brand-500"
              />
              Fast mode
            </label>
          </div>
        )}

        {/* Structure picker — categorized tree */}
        {!isRunning && !jobStatus?.results && (
          <div className="space-y-1">
            {CATEGORY_ORDER.filter((cat) => categories[cat]?.length > 0).map((category) => {
              const structs = categories[category] || []
              const selectedInCat = structs.filter((s) => selected.has(s.id)).length
              const allInCat = selectedInCat === structs.length
              const isExpanded = expanded.has(category)
              const isBone = BONE_CATEGORIES.includes(category)

              return (
                <div key={category} className="rounded-lg bg-gray-900/50 border border-gray-700/50">
                  {/* Category header */}
                  <div
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-700/30 rounded-t-lg"
                    onClick={() => toggleExpand(category)}
                  >
                    <svg
                      className={`w-3.5 h-3.5 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <input
                      type="checkbox"
                      checked={allInCat}
                      ref={(el) => { if (el) el.indeterminate = selectedInCat > 0 && !allInCat }}
                      onChange={() => toggleCategory(category)}
                      onClick={(e) => e.stopPropagation()}
                      className="accent-brand-500"
                    />
                    <span className="text-sm text-gray-300 font-medium flex-1">{category}</span>
                    <span className="text-[10px] text-gray-500">
                      {selectedInCat}/{structs.length}
                    </span>
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: CATEGORY_COLORS[category] || '#888' }}
                    />
                  </div>

                  {/* Structures in this category */}
                  {isExpanded && (
                    <div className="px-3 pb-2 space-y-0.5">
                      {structs.map((s) => (
                        <label
                          key={s.id}
                          className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-700/30 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selected.has(s.id)}
                            onChange={() => toggleStructure(s.id)}
                            className="accent-brand-500"
                          />
                          <span className="text-xs text-gray-400">{s.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Job progress */}
        {jobStatus && isRunning && (
          <div className="rounded-lg bg-gray-900 border border-gray-700 p-4 space-y-3">
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div
                className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${jobStatus.progress || 0}%` }}
              />
            </div>
            <p className="text-sm text-gray-300">{jobStatus.phase}</p>
            {jobStatus.elapsed > 0 && (
              <p className="text-xs text-gray-500">{jobStatus.elapsed}s elapsed</p>
            )}
          </div>
        )}

        {/* Complete — results */}
        {jobStatus?.status === 'complete' && jobStatus.results && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-green-400">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">Segmentation complete</span>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-green-900/30 rounded-lg p-2">
                <p className="text-lg font-bold text-green-400">{jobStatus.results.found}</p>
                <p className="text-[10px] text-gray-400">Segmented</p>
              </div>
              <div className="bg-gray-700/30 rounded-lg p-2">
                <p className="text-lg font-bold text-gray-400">{jobStatus.results.notFound}</p>
                <p className="text-[10px] text-gray-400">Not in scan</p>
              </div>
              {jobStatus.results.errors > 0 && (
                <div className="bg-red-900/30 rounded-lg p-2">
                  <p className="text-lg font-bold text-red-400">{jobStatus.results.errors}</p>
                  <p className="text-[10px] text-gray-400">Errors</p>
                </div>
              )}
            </div>

            {/* Per-structure results */}
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {jobStatus.results.structures
                .filter((s) => s.status === 'done' || s.modelId)
                .map((s) => (
                  <div key={s.id} className="flex items-center justify-between px-3 py-1.5 bg-gray-900/50 rounded">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-xs text-gray-300">{s.label}</span>
                    </div>
                    <span className="text-[10px] text-gray-500">
                      {s.vertexCount ? `${(s.vertexCount / 1000).toFixed(1)}K verts` : ''}
                    </span>
                  </div>
                ))}
              {jobStatus.results.structures
                .filter((s) => s.status === 'not_found')
                .map((s) => (
                  <div key={s.id} className="flex items-center gap-2 px-3 py-1.5 opacity-40">
                    <div className="w-2 h-2 rounded-full bg-gray-600" />
                    <span className="text-xs text-gray-500">{s.label} — not in scan</span>
                  </div>
                ))}
            </div>

            <a
              href={jobStatus.results.viewerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-medium transition-colors"
            >
              Open in 3D Viewer
            </a>
            <button
              onClick={() => { setJobId(null); setJobStatus(null) }}
              className="w-full text-center px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm transition-colors"
            >
              Run another segmentation
            </button>
          </div>
        )}

        {/* Failed */}
        {jobStatus?.status === 'failed' && (
          <div className="rounded-lg bg-gray-900 border border-gray-700 p-4 space-y-2">
            <p className="text-red-400 font-medium">Segmentation failed</p>
            <p className="text-sm text-gray-400">{jobStatus.error}</p>
            <button
              onClick={() => { setJobId(null); setJobStatus(null) }}
              className="w-full text-center px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm transition-colors"
            >
              Try again
            </button>
          </div>
        )}
      </div>

      {/* Footer — Segment button */}
      {(!jobStatus || jobStatus.status === 'failed') && (
        <div className="px-5 py-4 border-t border-gray-700">
          <button
            onClick={startSegmentation}
            disabled={selected.size === 0 || isRunning}
            className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg font-semibold transition-colors"
          >
            Segment {selected.size} Structure{selected.size !== 1 ? 's' : ''}
          </button>
          <p className="text-xs text-gray-500 mt-2 text-center">
            {fast ? 'Fast mode: lower accuracy, ~2-5 min' : 'Full accuracy: ~5-10 min'}
          </p>
        </div>
      )}
    </div>
  )
}
